<script setup>
import useVuelidate from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'next/button/Button.vue';
import Editor from 'next/wgpt/Automation/editor/Index.vue';
import { computed, reactive, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import ZidEditorSettings from './ZidEditorSettings.vue';
import TokenFailureBanner from './TokenFailureBanner.vue';

const props = defineProps({
  integration: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const route = useRoute();

const { t } = useI18n();
const uiFlags = useMapGetter('wgptIntegrations/getUIFlags');

const hook = computed(() => {
  const hooks = props.integration.hooks ?? [];
  return hooks[0];
});
const initialState = {
  automation: {
    trigger: structuredClone(toRaw(hook.value?.trigger || {})),
    steps: structuredClone(toRaw(hook.value?.steps || {})),
  },
};
const state = reactive({ ...initialState });
const rules = {
  automation: {
    trigger: {},
    steps: {},
  },
};

const v$ = useVuelidate(rules, state);

const hasPendingChanges = computed(() => {
  if (!hook.value) return false;

  return (
    JSON.stringify(state.automation.steps) !==
      JSON.stringify(hook.value.steps) ||
    JSON.stringify(state.automation.trigger) !==
      JSON.stringify(hook.value.trigger)
  );
});

const selected = computed(() => {
  return route.query.selected;
});

const handleSubmit = async () => {
  try {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    const hookUpdates = {
      wgpt_automation_attributes: {
        trigger: state.automation.trigger,
        steps: state.automation.steps,
      },
    };
    await store.dispatch('wgptIntegrations/updateZid', hookUpdates);
    const successMessage = t(
      'WGPT_INTEGRATIONS.ZID.EDITOR.API.SUCCESS_MESSAGE'
    );
    useAlert(successMessage);
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_INTEGRATIONS.ZID.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col">
    <header
      class="max-w-6xl w-full mx-auto flex justify-between pt-1 pb-4 px-6"
    >
      <h2 class="text-base font-semibold leading-6 text-n-slate-12">
        {{ t('WGPT_INTEGRATIONS.ZID.EDITOR.HEADER.TITLE') }}
      </h2>
      <div class="flex gap-2">
        <Button
          size="sm"
          blue
          :label="t('WGPT_INTEGRATIONS.ZID.EDITOR.HEADER.SAVE_BUTTON')"
          :is-loading="uiFlags.isUpdatingZid"
          :disabled="!hasPendingChanges || uiFlags.isUpdatingZid"
          @click.prevent="handleSubmit"
        />

        <router-link
          :to="{ query: { selected: 'settings' } }"
          :class="{
            'settings-button-active': selected === 'settings',
          }"
          replace
        >
          <Button
            variant="ghost"
            color="slate"
            icon="i-lucide-bolt"
            size="sm"
            class="settings-button"
            :title="t('WGPT_INTEGRATIONS.ZID.EDITOR.HEADER.SETTINGS_BUTTON')"
          />
        </router-link>
      </div>
    </header>
    <TokenFailureBanner :hook="hook" />
    <Editor
      v-model="state.automation"
      :dirty="hasPendingChanges"
      class="h-[80vh] border-t border-n-weak"
      disable-trigger
    >
      <template #customSidebarSettings>
        <ZidEditorSettings
          v-model="state.automation"
          :app-id="integration?.id"
          :hook-id="hook?.id || -1"
          :automation-id="hook?.automation_id"
          :config="hook?.config"
        />
      </template>
    </Editor>
  </div>
</template>

<style lang="scss" scoped>
.settings-button-active .settings-button {
  @apply outline-none outline-n-brand outline-offset-1;
}
.settings-button-invalid .settings-button {
  @apply outline-none outline-n-ruby-10 outline-offset-1;
}
</style>
